/**
 * Query keys for platform-scoped data.
 *
 * Platform keys are deliberately NOT namespaced by the active tenant, unlike
 * tenant-scoped keys which use `useTenantKey()`. Platform data is
 * tenant-independent by definition, so keying it by the selected tenant would
 * evict it whenever the user switched tenants on their way back to a tenant
 * route — and, worse, would imply the response depends on a tenant it does not.
 *
 * `list()` normalises its filter object so equivalent filters always produce
 * an identical key: React Query compares keys structurally, and `{a:1,b:2}`
 * and `{b:2,a:1}` would otherwise be two cache entries for one response.
 * Undefined values are dropped for the same reason — an omitted filter and an
 * explicitly-undefined one are the same request.
 *
 * Nothing sensitive belongs in a key: keys are held in memory, surfaced by
 * devtools, and would be written out by any future cache persister.
 */

/** Deterministic ordering, with empty values removed. */
function normalise(filters: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

export const fleetKeys = {
  all: ['fleet'] as const,
  /**
   * Keyed by TENANT and window. Unlike the platform keys below, this reads
   * tenant-scoped data, so switching tenant must refetch rather than show the
   * previous tenant's figures under the new name.
   */
  summary: (tenantId: string, hours: number) => ['fleet', 'summary', tenantId, hours] as const,
  /** Keyed by the selected vehicle too: the live panel is part of the response. */
  canOperations: (tenantId: string, days: number, vehicleId: string | undefined) =>
    ['fleet', 'can-operations', tenantId, days, vehicleId ?? 'auto'] as const,
};

export const platformKeys = {
  all: ['platform'] as const,

  tenants: {
    all: ['platform', 'tenants'] as const,
    lists: () => ['platform', 'tenants', 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      ['platform', 'tenants', 'list', normalise(filters)] as const,
    details: () => ['platform', 'tenants', 'detail'] as const,
    detail: (tenantId: string) => ['platform', 'tenants', 'detail', tenantId] as const,
  },

  users: {
    all: ['platform', 'users'] as const,
    lists: () => ['platform', 'users', 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      ['platform', 'users', 'list', normalise(filters)] as const,
    details: () => ['platform', 'users', 'detail'] as const,
    detail: (userId: string) => ['platform', 'users', 'detail', userId] as const,
  },

  /*
   * The partner console. Under `platform` only because these keys share its
   * namespace shape; the routes are partner-scoped and NOT tenant-keyed —
   * a partner has no tenant, and keying by the active tenant would cache one
   * partner's companies under whichever customer they last entered.
   */
  partner: {
    all: ['partner'] as const,
    context: () => ['partner', 'context'] as const,
    companies: () => ['partner', 'companies'] as const,
    branding: () => ['partner', 'branding'] as const,
    members: () => ['partner', 'members'] as const,
    /** The RESELLER level, not a fleet account — see the note on the endpoints. */
    resellers: () => ['partner', 'resellers'] as const,
    policyTemplates: () => ['partner', 'policy-templates'] as const,
    /**
     * One reseller's CEILING. Keyed by id, so opening a second dialog cannot serve the
     * first one's answer under the second one's heading.
     */
    resellerFeatures: (id: string) => ['partner', 'resellers', id, 'features'] as const,
  },

  whiteLabels: {
    all: ['platform', 'white-labels'] as const,
    lists: () => ['platform', 'white-labels', 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      ['platform', 'white-labels', 'list', normalise(filters)] as const,
    details: () => ['platform', 'white-labels', 'detail'] as const,
    detail: (id: string) => ['platform', 'white-labels', 'detail', id] as const,
    features: (id: string) => ['platform', 'white-labels', 'features', id] as const,
    members: (id: string) => ['platform', 'white-labels', 'members', id] as const,
    /** The partner's RESELLERS, read by the platform's record page. */
    resellers: (id: string) => ['platform', 'white-labels', 'resellers', id] as const,
  },

  audit: {
    all: ['platform', 'audit'] as const,
    list: (filters: Record<string, unknown>) =>
      ['platform', 'audit', 'list', normalise(filters)] as const,
  },

  /** No filters: the snapshot takes no parameters and describes the whole platform. */
  pipelineHealth: ['platform', 'pipeline-health'] as const,
} as const;
